import React from 'react';
import { Box, Text, TextArea } from 'grommet';
import useClippy from 'use-clippy';
import Icon from '@mdi/react';
import { mdiContentCopy } from '@mdi/js';
import { isItemNew } from '../../utils';
import {
  getAdUnitTemplate,
  getLegacyTemplate,
  ProgressionOverlay
} from './embedded-flow-config/configuration-template-button';
import { PAGEOVERLAY } from './embedded-flow-config/embedded-flow-config.actions';

const SCRIPT_TEMPLATES = {
  PAGE_EMBED: configId => `
    <script>
      const fc = new FlowContainer({
          params: {
            email: '<INSERT EMAIL ADDRESS>'
          },
      });
      </script>

      // Insert this code snippet at the location where you want the embed to appear.
      <div data-af-config-id="${configId}"></div>
  `,
  PAGE_OVERLAY: configId => `
    <script>
      const fc = new FlowContainer({
          params: {
            email: '<INSERT EMAIL ADDRESS>'
          },
      });
      fc.showById("${configId}")
    </script>
  `
};

// Custom hook for script generation logic
const useScriptGeneration = (item, templates) => {
  const configId = !isItemNew(item.id)
    ? item.id
    : 'Configuration ID will be generated upon saving.';

  const template =
    item.adUnitTemplateId !== null
      ? getAdUnitTemplate(templates, item.adUnitTemplateId)
      : getLegacyTemplate(item);

  const isOverlayTemplate =
    template?.implementationType === PAGEOVERLAY ||
    template === ProgressionOverlay;

  const generatedScript = isOverlayTemplate
    ? SCRIPT_TEMPLATES.PAGE_OVERLAY(configId)
    : SCRIPT_TEMPLATES.PAGE_EMBED(configId);

  return {
    generatedScript
  };
};

// Custom hook for clipboard functionality
const useClipboardCopy = addSuccess => {
  const [, setClipboard] = useClippy();

  const copyToClipboard = script => {
    setClipboard(script);
    addSuccess('Body copied to clipboard');
  };

  return { copyToClipboard };
};

// Script display component
const ScriptDisplay = ({ script, onCopy }) => (
  <Box
    background={{ color: 'white300' }}
    direction='row'
    style={{ position: 'relative' }}
  >
    <TextArea
      rows={script.split('\n').length || 10}
      background={{ color: 'white300' }}
      style={{ height: '100%' }}
      value={script}
      disabled={true}
    />
    <Box
      style={{
        position: 'absolute',
        right: '10px',
        top: '10px'
      }}
    >
      <Icon
        path={mdiContentCopy}
        size={'20px'}
        cursor='pointer'
        onClick={onCopy}
      />
    </Box>
  </Box>
);

// Main component
const IntegrationScriptBody = ({ item, addSuccess, templates }) => {
  const { generatedScript } = useScriptGeneration(item, templates);
  const { copyToClipboard } = useClipboardCopy(addSuccess);

  const handleBodyCopy = () => {
    copyToClipboard(generatedScript);
  };

  return (
    <Box data-testid='script-body'>
      <Box margin={{ bottom: 'small' }} gap='small'>
        <Text weight='bold'>Integration Script - Body Section</Text>
        <Text>
          Place the below block of code in the {'<body>'} section of your
          website.
        </Text>
      </Box>
      <Text weight='bold' size='small'>
        {'<body>'}
      </Text>
      <ScriptDisplay script={generatedScript} onCopy={handleBodyCopy} />
      <Text weight='bold' size='small'>
        {'</body>'}
      </Text>
      <Text margin={{ top: 'xsmall' }}>
        Don't forget to put the partner header scripts on the page too, if you
        haven't done so already.
      </Text>
    </Box>
  );
};

export default IntegrationScriptBody;
